import React, {ReactNode} from 'react'
import {
  Authenticator,
  CheckboxField,
  useAuthenticator
} from '@aws-amplify/ui-react'
import AppFooter from '../layout/AppFooter'
import BrandLogo from '../../assets/placemakr-horizontal-logo-dusk.png'
import {signUp, signIn} from 'aws-amplify/auth'

interface AuthWrapperProps {
  children?: ReactNode
  register?: boolean
}

const components = {
  Header() {
    return (
      <div className="flex justify-center py-2 sm:py-4">
        <img src={BrandLogo} className="w-48" />
      </div>
    )
  },
  Footer() {
    return <AppFooter />
  },
  SignUp: {
    FormFields() {
      const {validationErrors} = useAuthenticator()
      return (
        <>
          <Authenticator.SignUp.FormFields />
          <CheckboxField
            errorMessage={validationErrors.terms_confirmation as string}
            hasError={!!validationErrors.terms_confirmation}
            name="terms_confirmation"
            value="yes"
            label="I agree to the Terms of Service"
            required={true}
          />
        </>
      )
    }
  }
}

const formFields = {
  signIn: {
    username: {
      label: 'Username (email)',
      labelHidden: false,
      placeholder: 'Enter your email',
      order: 1
    },
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 2
    }
  },
  signUp: {
    username: {
      label: 'Username (email)',
      labelHidden: false,
      placeholder: 'Enter your email',
      order: 1
    },
    given_name: {
      label: 'First Name',
      labelHidden: false,
      placeholder: 'Enter your first name',
      order: 2
    },
    family_name: {
      label: 'Last Name',
      labelHidden: false,
      placeholder: 'Enter your last name',
      order: 3
    },
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 4
    },
    confirm_password: {
      label: 'Confirm Password',
      labelHidden: false,
      placeholder: 'Confirm your password',
      order: 5
    }
  },
  forceNewPassword: {
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your Password'
    }
  },
  resetPassword: {
    username: {
      label: 'Username (email)',
      labelHidden: false,
      placeholder: 'Enter your email'
    }
  },
  confirmResetPassword: {
    confirmation_code: {
      label: 'Confirmation Code',
      labelHidden: false,
      placeholder: 'Enter your confirmation code',
      order: 1
    },
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 2
    },
    confirm_password: {
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 3
    }
  },
  confirmSignIn: {
    confirmation_code: {
      label: 'Confirmation Code',
      labelHidden: false,
      placeholder: 'Enter your confirmation code'
    }
  }
}

/**
 * Overrides for signUp and signIn function calls.  This is a workaround that
 * allows case-insensitive usernames during login and signup.
 */
const services = {
  async handleSignUp(input: any) {
    // convert email/username to lowercase
    const lowerUsername = input.username.toLowerCase()
    return signUp({
      username: lowerUsername,
      password: input.password,
      options: {
        ...input.options,
        autoSignIn: true
      }
    })
  },

  async handleSignIn(input: any) {
    // convert username to lowercase
    const lowerUsername = input.username.toLowerCase()
    return signIn({
      username: lowerUsername,
      password: input.password
    })
  }
}

/**
 * The AuthWrapper is used to "wrap" content to ensure that it is accessed by
 * an authenticated user.  It uses the AWS Amplify "Authenticator" connected
 * component, which handles many facets associated with user authentication
 * and management including: login, registration, email confirmation codes,
 * forgotten credentials, password reset, etc.
 *
 * @see:  https://ui.docs.amplify.aws/react/connected-components/authenticator/customization
 * @param children React nodes including the content requiring user authentication
 * @param register Determines whether registration is enabled or not
 */
const AuthWrapper = ({children, register = false}: AuthWrapperProps) => {
  const initialState = register ? 'signUp' : 'signIn'
  return (
    <Authenticator
      services={services}
      initialState={initialState}
      formFields={formFields}
      components={components}
      hideSignUp={!register}
    >
      {children}
    </Authenticator>
  )
}

export default AuthWrapper
